import { useQuery } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";

export interface UsageSummary {
  period_days: number;
  totals: {
    requests: number;
    input_tokens: number;
    output_tokens: number;
    estimated_cost_usd: number;
    active_users: number;
  };
  providers: Record<
    string,
    {
      total_input_tokens: number;
      total_output_tokens: number;
      total_requests: number;
      estimated_cost_usd: number;
      by_endpoint: Record<
        string,
        { input_tokens: number; output_tokens: number; requests: number; estimated_cost_usd: number }
      >;
    }
  >;
}

export interface UserUsage {
  user_id: string;
  name: string;
  email: string;
  requests: number;
  input_tokens: number;
  output_tokens: number;
  estimated_cost_usd: number;
}

export interface TimelinePoint {
  timestamp: string;
  requests: number;
  input_tokens: number;
  output_tokens: number;
  estimated_cost_usd: number;
}

export function useAdminUsageSummary(days: number) {
  return useQuery<UsageSummary>({
    queryKey: ["admin", "usage", "summary", days],
    queryFn: () =>
      apiClient
        .get("api/admin/usage/summary", { searchParams: { days } })
        .json(),
    retry: false,
  });
}

export function useAdminUsageByUser(days: number) {
  return useQuery<{ period_days: number; users: UserUsage[] }>({
    queryKey: ["admin", "usage", "by-user", days],
    queryFn: () =>
      apiClient
        .get("api/admin/usage/by-user", { searchParams: { days } })
        .json(),
    retry: false,
  });
}

export function useAdminUsageTimeline(days: number, bucket: "day" | "hour" = "day") {
  return useQuery<{ period_days: number; bucket: string; series: TimelinePoint[] }>({
    queryKey: ["admin", "usage", "timeline", days, bucket],
    queryFn: () =>
      apiClient
        .get("api/admin/usage/timeline", { searchParams: { days, bucket } })
        .json(),
    retry: false,
  });
}

export interface FeatureUsage {
  feature: string;
  requests: number;
  input_tokens: number;
  output_tokens: number;
  estimated_cost_usd: number;
}

export function useAdminUsageByFeature(days: number) {
  return useQuery<{ period_days: number; features: FeatureUsage[] }>({
    queryKey: ["admin", "usage-by-feature", days],
    queryFn: () =>
      apiClient.get("api/admin/usage/by-feature", { searchParams: { days } }).json(),
    retry: false,
  });
}

export interface ActivityEndpoint {
  endpoint: string;
  requests: number;
  input_tokens: number;
  output_tokens: number;
  estimated_cost_usd: number;
}

export interface ActivityItem {
  feature: string;
  runs: number;
  run_users: number;
  requests: number;
  input_tokens: number;
  output_tokens: number;
  estimated_cost_usd: number;
  usage_users: number;
  endpoints: ActivityEndpoint[];
}

export function useAdminActivity(days: number) {
  return useQuery<{ period_days: number; items: ActivityItem[] }>({
    queryKey: ["admin", "usage-activity", days],
    queryFn: () =>
      apiClient.get("api/admin/usage/activity", { searchParams: { days } }).json(),
    retry: false,
  });
}

export interface TesterRollupUser {
  user_id: string;
  email: string;
  plan: string | null;
  tags: string[];
  runs_7d: number;
  cost_today_usd: number;
  cost_7d_usd: number;
  cost_period_usd: number;
  avg_daily_prior_7d_usd: number;
  alert: boolean;
  daily: { date: string; cost_usd: number }[];
}

export function useAdminTesterRollup(days: number) {
  return useQuery<{ period_days: number; users: TesterRollupUser[] }>({
    queryKey: ["admin", "usage-tester-rollup", days],
    queryFn: () =>
      apiClient.get("api/admin/usage/tester-rollup", { searchParams: { days } }).json(),
    retry: false,
  });
}
